import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const { user, ready } = useAuth();
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    if (!ready) return;  // wait until auth check completes
    if (!user) { setCart({ items: [], total: 0 }); setLoaded(true); return; }
    try {
      const { data } = await api.get("/cart");
      setCart(data);
    } catch { /* ignore */ }
    finally { setLoaded(true); }
  }, [user, ready]);

  useEffect(() => { load(); }, [load]);

  const add = async (product_id, qty = 1) => {
    if (!user) { toast.error("Please sign in to add items"); return false; }
    const { data } = await api.post("/cart/add", { product_id, qty });
    setCart(data);
    toast.success("Added to your basket");
    return true;
  };

  const update = async (product_id, qty) => {
    const { data } = await api.put("/cart/item", { product_id, qty });
    setCart(data);
  };

  const remove = async (product_id) => {
    const { data } = await api.delete(`/cart/item/${product_id}`);
    setCart(data);
  };

  const count = cart.items.reduce((a, i) => a + i.qty, 0);

  return (
    <CartContext.Provider value={{ cart, add, update, remove, load, count, loaded }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
